import { Injectable } from '@angular/core';
import { map, type Observable, switchMap, tap } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from './storage.service';
import { StorageEnum } from '../enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from './util.service';
import { type CreatePassword } from '../models/create-passord';
import { type RegisterAccount } from '../models/register-account';
import { type RegisterAccountCredentials } from '../models/register-account-credentials';
import { type Account } from '../models/account';
import { isWithinInterval, subDays } from 'date-fns';
import { ServiceAddress } from '../models/service-address';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Router } from '@angular/router';
import { Nav } from '../enums/nav.enum';
import { type UserProfile } from '../models/user-profile';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { MatDialog } from '@angular/material/dialog';
import { DialogResultType } from '../enums/dialogresulttype.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { ChatbotService } from './chatbot.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { LoggingService } from './logging.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { EncryptionService } from './encryption.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private usernameSet = false;
  private pwSet = false;

  constructor(
    private readonly dialog: MatDialog,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly utils: UtilService,
    private readonly chatbot: ChatbotService,
    private readonly loggingService: LoggingService,
    private readonly encryptionService: EncryptionService,
  ) {}

  /**
   * Returns content from Builder
   * @param model model name in builder
   * @param pageName content name in builder
   * @returns observable containing
   */
  getAUP(userLoginName: string, userPassword: string, startIdx = '1', endIdx = '30'): Observable<UserProfile> {
    const url = environment.AuthUrl() + '/authenticate/aup';
    const authData = {
      UserName: userLoginName,
      Password: userPassword,
      startIdx,
      endIdx,
    };

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };

    const res = this.http.post(url, authData, httpOptions);
    return res.pipe(
      tap((data: any) => {
        if (!this.usernameSet) {
          this.storage.setSession(StorageEnum.UserName, userLoginName);
          this.usernameSet = true;
        }

        if (!this.pwSet) {
          this.storage.setSession(StorageEnum.UserPW, userPassword);
          this.pwSet = true;
        }

        if (data.TotalNumAccounts > 1) {
          const sortedData = this.sortMultiAccount(data.Accounts);
          data.Accounts = sortedData;
          this.storage.setSession(StorageEnum.MultiAccountResponse, data);
          this.loggingService.setAuthenticatedUserContext(userLoginName);
        } else {
          this.loggingService.setAuthenticatedUserContext(userLoginName, data.Accounts[0].AccountNumber);
          this.storage.setSession(StorageEnum.LoginResponse, data);
          environment.AuthToken = data.AuthToken ?? '';
        }
      }),
    );
  }

  searchAccountAUA(accountNumber: string, userLoginName: string): Observable<any> {
    const url = environment.OASUrl() + '/account/aua';
    const authData = {
      AccountId: accountNumber,
      UserName: userLoginName,
    };

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };

    const res = this.http.post(url, authData, httpOptions);
    return res;
  }

  forgotUsernamePassword(email: string): Observable<any> {
    const url = environment.AuthUrl() + '/authenticate/forgot';
    const authData = {
      EmailID: email,
    };

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };

    const res = this.http.post(url, authData, httpOptions);
    return res.pipe(
      tap((data: any) => {
        this.utils.consoleGroup('fup auth service data: ', data);
      }),
    );
  }

  resetPassword(resetData: CreatePassword): Observable<any> {
    const url = environment.AuthUrl() + '/authenticate/reset';

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };

    const res = this.http.post(url, resetData, httpOptions);
    return res.pipe(
      tap((data: any) => {
        this.utils.consoleGroup('reset password auth service data: ', data);
      }),
    );
  }

  registerAccount(registerAccountRequest: RegisterAccount): Observable<any> {
    const url = environment.AuthUrl() + '/authenticate/verify-account';

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };
    const res = this.http.post(url, registerAccountRequest, httpOptions);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data.Status;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  registerAccountCredentials(registerAccountRequest: RegisterAccountCredentials): Observable<any> {
    const url = environment.AuthUrl() + '/authenticate/register';

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };
    const res = this.http.post(url, registerAccountRequest, httpOptions);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data.Status;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  lookupAccount(accountNumber: string, postalCode: string): Observable<any> {
    const url = environment.AuthUrl() + '/authenticate/validate-account';
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };
    return this.encryptionService.encryptValue(accountNumber).pipe(
      switchMap((encryptionResponse: any) => {
        const encryptedAccountNumber = encryptionResponse?.EncryptedText;
        if (!encryptedAccountNumber) {
          throw new Error('ENCRYPTION_FAILED');
        }

        const lookupData = {
          AccountId: encryptedAccountNumber,
          ZipCode: postalCode,
        };

        return this.http.post(url, lookupData, httpOptions);
      }),
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  /**
   * Logs out the current user by clearing session storage, cookies, and optionally navigating to login.
   * @param routeToLogin - If true (default), navigates to the login page after logout.
   *                       Set to false when already on the login page to preserve query params.
   */
  logout(routeToLogin: boolean = true): void {
    this.utils.consoleLog('Logging out!');
    this.closeDialogs();
    this.utils.showSpinner();
    this.storage.clearAllSession();
    this.storage.clear();
    this.pwSet = false;
    this.usernameSet = false;
    this.chatbot.clearAllCookies();
    if (routeToLogin) {
      void this.router.navigateByUrl(Nav.Login);
    }
  }

  /**
   * Helper Methods
   */

  /**
   * Closes all open dialogs with a cancel result
   * closeAll method on MatDialog closes with no result, which results in error
   */
  closeDialogs(): void {
    const dialogs = this.dialog.openDialogs;
    dialogs.forEach((dialog) => {
      dialog.close(DialogResultType.Cancel);
    });
  }

  /**
   * Retrieves main service address and if that SA is seasonal
   * @param account AUP Response
   * @returns Main "Dominant" Service address on account and if that account is seasonal
   */
  getServiceAddress(account: Account): [ServiceAddress, boolean] {
    const serviceAddresses: ServiceAddress[] = account.ServiceAddresses;

    const active = serviceAddresses.find((sa: ServiceAddress) => {
      return sa.SaStatusFlag === '20';
    });

    if (active) {
      return [active, false];
    }

    const reactivated = serviceAddresses.find((sa: ServiceAddress) => {
      return sa.SaStatusFlag === '50';
    });

    if (reactivated) {
      return [reactivated, false];
    }

    const stopped = serviceAddresses.find((sa: ServiceAddress) => {
      return sa.SaStatusFlag === '40';
    });

    if (stopped) {
      return [stopped, false];
    }

    const pending = serviceAddresses.find((sa: ServiceAddress) => {
      return sa.SaStatusFlag === '10';
    });

    if (pending) {
      return [pending, false];
    }

    const seasonal = serviceAddresses.find((sa: ServiceAddress) => {
      const inInterval = isWithinInterval(new Date(sa.SaEndDate ?? ''), {
        start: subDays(new Date(), 240),
        end: new Date(),
      });

      return account.Seasonal && inInterval && (sa.SaStatusFlag === '60' || sa.SaStatusFlag === '70');
    });

    if (seasonal) {
      return [seasonal, true];
    }

    const closed = serviceAddresses.find((sa: ServiceAddress) => {
      return sa.SaStatusFlag === '60' || sa.SaStatusFlag === '70';
    });

    if (closed) {
      return [closed, false];
    }

    this.utils.consoleLog('ERROR: getServiceAddress() failed to find account.');
    return [new ServiceAddress(), false];
  }

  sortMultiAccount(accounts: Account[]): Account[] {
    const accountsCopy = JSON.parse(JSON.stringify(accounts));
    accountsCopy.sort((a: Account, b: Account) => {
      let aAddress: any, bAddress: any;

      if (a.NumAgreements ?? 0 > 1) {
        aAddress = this.getServiceAddress(a)[0];
      } else {
        aAddress = a.ServiceAddresses[0];
      }

      if (b.NumAgreements ?? 0 > 1) {
        bAddress = this.getServiceAddress(b)[0];
      } else {
        bAddress = b.ServiceAddresses[0];
      }

      return this.compareStatus(aAddress.SaStatusFlag, bAddress.SaStatusFlag);
    });
    return accountsCopy;
  }

  compareStatus(a?: number | string, b?: number | string): number {
    if (a && b) {
      return this.compareStatusHelper(a) < this.compareStatusHelper(b) ? -1 : 1;
    }
    return 0;
  }

  /**
   * Assigns numerical value to SA Status based on order we want to display accounts
   * @param status SaStatusFlag of SA
   * @returns number to sort SA on
   */
  compareStatusHelper(status: string | number): number {
    switch (status) {
      case '10':
        return 1;
      case '20':
        return 2;
      case '50':
        return 3;
      case '40':
        return 4;
      case '60':
      case '70':
        return 5;
      default:
        return 6;
    }
  }
}
